/** @jsxImportSource react */
'use client';

import Image from 'next/image';
import Link from 'next/link';

import { motion } from 'motion/react';

// Real customer churches who said yes to logo usage in the March 2026 survey.
// All have NPS 7+ and wrote a testimonial. Logo files live in /public/logos/.
//
// Sizing notes:
//  - Each logo is rendered at the same visual height (~36px); width is fluid.
//  - Logos are desaturated by default for visual consistency, and pop into full
//    color on hover so visitors can read the wordmarks.
interface Church {
  name: string;
  src: string;
  alt: string;
  // Per-logo width tweak — logo aspect ratios vary wildly. Tune in pixels.
  width: number;
  height: number;
}

const CHURCHES: Church[] = [
  {
    alt: 'Harvest',
    height: 36,
    name: 'Harvest',
    src: '/logos/harvest.png',
    width: 140,
  },
  {
    alt: 'Northwood Church',
    height: 36,
    name: 'Northwood',
    src: '/logos/northwood.webp',
    width: 150,
  },
  {
    alt: 'Life Church',
    height: 36,
    name: 'Life Church',
    src: '/logos/life-church.webp',
    width: 130,
  },
  {
    alt: 'Discover Church',
    height: 36,
    name: 'Discover Church',
    src: '/logos/discover-church.png',
    width: 160,
  },
  {
    alt: 'Harmony Hill Church',
    height: 36,
    name: 'Harmony Hill',
    src: '/logos/harmony-hill.svg',
    width: 130,
  },
];

export function CustomerStrip() {
  return (
    <section className="border-y border-[color:var(--color-border-soft)] bg-white py-12 sm:py-14">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-5 sm:px-8 lg:px-20">
        <motion.span
          className="text-[12px] font-medium uppercase tracking-[0.18em] text-[color:var(--color-gray-med)]"
          initial={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ margin: '-50px', once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          Used by churches running real growth tracks
        </motion.span>

        {/* Static row — all 5 logos always visible. Was a marquee, but
            with 5 churches the loop made it so only 3 were on screen at
            any moment. Visitors didn't see the full lineup. Static
            renders better and matches the Stripe / Linear / Vercel
            social-proof pattern. */}
        <div className="flex w-full flex-wrap items-center justify-center gap-x-10 gap-y-6 sm:gap-x-14 lg:gap-x-20">
          {CHURCHES.map(church => (
            <Link
              key={church.name}
              aria-label={`Read ${church.name}'s OnArk story`}
              className="shrink-0 opacity-60 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
              href="/customers"
            >
              <Image
                alt={church.alt}
                className="h-9 w-auto object-contain"
                height={church.height}
                src={church.src}
                unoptimized={church.src.endsWith('.svg')}
                width={church.width}
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
